import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Package, ShieldCheck, Loader2, Truck, CreditCard, Wallet,
  CheckCircle2, Star, ArrowRight, Box
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL, API } from '@/lib/backendUrl';


export default function GuestProductPage() {
  const { token } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [purchased, setPurchased] = useState(false);
  const [payMethod, setPayMethod] = useState('card');
  const [form, setForm] = useState({ guest_name: '', guest_email: '', delivery_address: '' });

  useEffect(() => {
    fetch(`${API}/api/vendor/payments/qr/${token}`)
      .then(r => r.json())
      .then(d => {
        setProduct(d.product);
        // Track QR scan event for attribution
        if (d.product?.vendor_id) {
          const visitorId = localStorage.getItem('bitsy_guest_id') || `qr_${Date.now()}`;
          fetch(`${API}/api/vendor/analytics/track`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ vendor_id: d.product.vendor_id, event_type: 'qr_scan', product_id: d.product.product_id, visitor_id: visitorId, metadata: { room: d.product.room_number, hotel: d.product.hotel_name, token } }),
          }).catch(() => {});
          fetch(`${API}/api/vendor/analytics/track`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ vendor_id: d.product.vendor_id, event_type: 'product_page_view', product_id: d.product.product_id, visitor_id: visitorId }),
          }).catch(() => {});
        }
      })
      .catch(() => toast.error('Product not found'))
      .finally(() => setLoading(false));
  }, [token]);

  const handlePurchase = async () => {
    if (!form.guest_name || !form.guest_email) { toast.error('Name and email required'); return; }
    setPurchasing(true);
    try {
      const price = product?.product_details?.unit_cost || 0;
      const res = await fetch(`${API}/api/vendor/payments/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          qr_token: token,
          amount: price,
          payment_method: payMethod,
          guest_name: form.guest_name,
          guest_email: form.guest_email,
          delivery_address: form.delivery_address,
        }),
      });
      const data = await res.json();
      if (data.success) {
        // Auto-settle for demo
        const pid = data.purchase.purchase_id;
        if (payMethod === 'card') {
          await fetch(`${API}/api/vendor/payments/purchase/${pid}/settle-fiat`, { method: 'POST' });
        }
        setPurchased(true);
        toast.success('Order placed successfully!');
      } else {
        toast.error(data.detail || 'Purchase failed');
      }
    } catch { toast.error('Purchase failed'); }
    finally { setPurchasing(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#F7931A]" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center text-gray-500">
        Product not found or QR code expired
      </div>
    );
  }

  if (purchased) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center px-4">
        <Card className="max-w-md w-full bg-white/[0.03] border-emerald-500/20" data-testid="purchase-success">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Order Confirmed!</h2>
            <p className="text-sm text-gray-400 mb-4">
              Your <span className="text-white">{product.product_name}</span> will be delivered to your address.
            </p>
            <div className="bg-white/[0.03] rounded-lg p-3 mb-4">
              <p className="text-[10px] text-gray-500">Vendor</p>
              <p className="text-sm text-white">{product.vendor_name}</p>
            </div>
            <div className="flex items-center justify-center gap-2 text-[10px] text-gray-500">
              <ShieldCheck className="w-3 h-3 text-emerald-400" />
              Verified by HERMES Protocol on-chain
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const price = product.product_details?.unit_cost || 0;
  const categoryColors = {
    mattress: 'from-purple-500/20 to-blue-500/20',
    linen: 'from-amber-500/20 to-orange-500/20',
    towel: 'from-cyan-500/20 to-teal-500/20',
    pillow: 'from-indigo-500/20 to-violet-500/20',
    body_care: 'from-pink-500/20 to-rose-500/20',
    ffe: 'from-[#F7931A]/20 to-amber-500/20',
  };
  const gradient = categoryColors[product.category] || 'from-gray-500/20 to-gray-600/20';

  return (
    <div className="min-h-screen bg-[#030712] text-white" data-testid="guest-product-page">
      {/* Hero */}
      <div className={`bg-gradient-to-br ${gradient} border-b border-white/5`}>
        <div className="max-w-lg mx-auto px-4 py-8 text-center">
          <Badge variant="outline" className="text-[9px] h-5 border-white/10 text-gray-400 mb-3">
            {product.hotel_name} | Room {product.room_number}
          </Badge>
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-white/[0.06] border border-white/10 flex items-center justify-center">
            <Package className="w-10 h-10 text-white/60" />
          </div>
          <h1 className="text-2xl font-bold heading-font mb-1">{product.product_name}</h1>
          <p className="text-sm text-gray-400">by {product.vendor_name}</p>
          <div className="flex items-center justify-center gap-1 mt-2">
            {[1,2,3,4,5].map(i => <Star key={i} className="w-3.5 h-3.5 text-[#F7931A] fill-[#F7931A]" />)}
            <span className="text-[10px] text-gray-500 ml-1">Hotel-verified quality</span>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {/* Product Info */}
        <Card className="bg-white/[0.03] border-white/5">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[9px] text-gray-500 uppercase tracking-wider">You experienced this at</p>
                <p className="text-sm text-white">{product.hotel_name}, Room {product.room_number}</p>
              </div>
              <Badge className="bg-[#F7931A]/10 text-[#F7931A] border-[#F7931A]/20 text-[10px]">
                {product.category}
              </Badge>
            </div>
            {product.product_details?.model && (
              <div>
                <p className="text-[9px] text-gray-500">Model</p>
                <p className="text-xs text-gray-300">{product.product_details.model}</p>
              </div>
            )}
            {product.product_details?.description && (
              <p className="text-[11px] text-gray-400 leading-relaxed">{product.product_details.description}</p>
            )}
            {product.vendor_description && (
              <p className="text-[10px] text-gray-500 italic">{product.vendor_description}</p>
            )}
            <div className="flex items-center gap-2 pt-2 border-t border-white/5">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span className="text-[10px] text-emerald-400">Verified on HERMES Protocol | Tracked via Room SBT</span>
            </div>
          </CardContent>
        </Card>

        {/* 3D Showroom CTA */}
        {product.vendor_id && (
          <Link
            to={`/vendors/${product.vendor_id}/showroom?from=qr`}
            className="flex items-center gap-3 p-3.5 rounded-xl border border-[#F7931A]/15 bg-gradient-to-r from-[#F7931A]/5 to-transparent hover:from-[#F7931A]/10 transition-all group"
            data-testid="showroom-cta"
          >
            <div className="w-10 h-10 rounded-lg bg-[#F7931A]/10 border border-[#F7931A]/20 flex items-center justify-center shrink-0">
              <Box className="w-5 h-5 text-[#F7931A]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-white">Explore All Products in 3D</p>
              <p className="text-[10px] text-gray-500">Visit {product.vendor_name}'s virtual showroom</p>
            </div>
            <ArrowRight className="w-4 h-4 text-[#F7931A] opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
          </Link>
        )}

        {/* Learn-to-Earn Claim */}
        {product.vendor_id && (
          <Link
            to={`/earn?claim=1&product=${product.product_id}&vendor=${product.vendor_id}&hotel=${product.hotel_id || ''}&room=${product.room_number || ''}`}
            className="flex items-center gap-3 p-3.5 rounded-xl border border-emerald-500/15 bg-gradient-to-r from-emerald-500/5 to-transparent hover:from-emerald-500/10 transition-all group"
            data-testid="claim-reward-cta"
          >
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
              <Wallet className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-white">Claim Reward</p>
              <p className="text-[10px] text-gray-500">Earn HERMES tokens for exploring this product</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-sm font-bold text-emerald-400">+2</p>
              <p className="text-[8px] text-gray-600">HERMES</p>
            </div>
          </Link>
        )}

        {/* Price & Purchase */}
        {price > 0 && (
          <Card className="bg-white/[0.03] border-[#F7931A]/10">
            <CardContent className="p-4 space-y-4">
              <div className="text-center">
                <p className="text-[9px] text-gray-500 uppercase tracking-wider">Get it delivered home</p>
                <p className="text-3xl font-bold text-[#F7931A] heading-font">${price.toLocaleString()}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">Free delivery | Direct from {product.vendor_name}</p>
              </div>

              {/* Payment Method */}
              <div>
                <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-2">Payment</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'card', icon: CreditCard, label: 'Credit Card', sub: 'Stripe' },
                    { id: 'crypto', icon: Wallet, label: 'Crypto', sub: '0xSplits' },
                  ].map(m => (
                    <button
                      key={m.id}
                      onClick={() => setPayMethod(m.id)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-left transition-all ${
                        payMethod === m.id
                          ? 'bg-[#F7931A]/10 border-[#F7931A]/30 text-[#F7931A]'
                          : 'bg-white/[0.02] border-white/8 text-gray-500'
                      }`}
                      data-testid={`pay-${m.id}`}
                    >
                      <m.icon className="w-4 h-4 shrink-0" />
                      <div>
                        <p className="text-xs font-medium">{m.label}</p>
                        <p className="text-[8px] opacity-60">{m.sub}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Form */}
              <div className="space-y-2">
                <input
                  type="text" placeholder="Your name"
                  value={form.guest_name}
                  onChange={e => setForm(f => ({ ...f, guest_name: e.target.value }))}
                  className="w-full h-9 bg-white/[0.04] border border-white/8 rounded-lg px-3 text-xs text-white placeholder:text-gray-600 focus:border-[#F7931A]/40 focus:outline-none"
                  data-testid="guest-name-input"
                />
                <input
                  type="email" placeholder="Email"
                  value={form.guest_email}
                  onChange={e => setForm(f => ({ ...f, guest_email: e.target.value }))}
                  className="w-full h-9 bg-white/[0.04] border border-white/8 rounded-lg px-3 text-xs text-white placeholder:text-gray-600 focus:border-[#F7931A]/40 focus:outline-none"
                  data-testid="guest-email-input"
                />
                <input
                  type="text" placeholder="Delivery address"
                  value={form.delivery_address}
                  onChange={e => setForm(f => ({ ...f, delivery_address: e.target.value }))}
                  className="w-full h-9 bg-white/[0.04] border border-white/8 rounded-lg px-3 text-xs text-white placeholder:text-gray-600 focus:border-[#F7931A]/40 focus:outline-none"
                  data-testid="delivery-address-input"
                />
              </div>

              <Button
                onClick={handlePurchase}
                disabled={purchasing}
                className="w-full h-11 bg-[#F7931A] hover:bg-[#e8841a] text-black font-semibold text-sm gap-2"
                data-testid="buy-btn"
              >
                {purchasing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
                Buy & Ship to My Address
                <ArrowRight className="w-4 h-4" />
              </Button>

              <p className="text-[9px] text-gray-600 text-center">
                Auto-split via {payMethod === 'card' ? 'Stripe Connect' : '0xSplits'} — verified on Room SBT
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
